/**
 * Client-side cookie utilities for secure browser cookie management.
 *
 * This module provides:
 * - Safe cookie reading/writing with SSR compatibility
 * - Automatic URL encoding/decoding
 * - Secure cookie configuration (HTTPS, SameSite, Path)
 * - Environment-aware security settings
 *
 * Security features:
 * - Automatic Secure flag for HTTPS environments
 * - SameSite=Lax for CSRF protection
 * - Path=/ for application-wide availability
 * - Proper URL encoding to handle special characters
 */

/**
 * Cookie configuration options for fine-tuned control over cookie behavior.
 */
export interface CookieOptions {
  /** Maximum age in seconds (defaults to session cookie if not specified) */
  maxAge?: number
  /** Cookie path (defaults to '/') */
  path?: string
  /** SameSite policy (defaults to 'Lax' for security) */
  sameSite?: 'Strict' | 'Lax' | 'None'
  /** Force secure flag regardless of protocol (defaults to auto-detect HTTPS) */
  secure?: boolean
  /** HttpOnly flag - prevents JavaScript access (defaults to false for client-side cookies) */
  httpOnly?: boolean
}

/**
 * Safely retrieves and decodes a cookie value by name from the browser.
 *
 * @param name - The name of the cookie to retrieve
 * @returns The decoded cookie value, or null if not found or in SSR context
 *
 * Implementation details:
 * - Returns null during SSR (when document is undefined)
 * - Uses string manipulation to parse document.cookie format
 * - Automatically URL-decodes the cookie value
 * - Handles edge cases like empty values and malformed cookies
 */
export function getCookie(name: string): string | null {
  // Guard against server-side rendering where document doesn't exist
  if (typeof document === 'undefined') return null

  // Prepend semicolon to make parsing easier - document.cookie format: "name1=value1; name2=value2"
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)

  // If cookie exists, parts will have length 2: [before_cookie, after_cookie_equals]
  if (parts.length === 2) {
    // Extract value up to next semicolon and decode URI components
    const cookieValue = parts.pop()?.split(';').shift() || null
    return cookieValue ? decodeURIComponent(cookieValue) : null
  }
  return null
}

/**
 * Safely sets a cookie with secure defaults and configurable options.
 *
 * @param name - The name of the cookie
 * @param value - The value to store (will be URL-encoded)
 * @param options - Optional cookie configuration
 *
 * Security considerations:
 * - Automatically applies Secure flag for HTTPS environments
 * - Uses SameSite=Lax by default for CSRF protection
 * - URL-encodes values to handle special characters safely
 * - Sets Path=/ for application-wide cookie availability
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {},
): void {
  // Guard against server-side rendering where document doesn't exist
  if (typeof document === 'undefined') return

  // Extract options with secure defaults
  const {
    maxAge,
    path = '/',
    sameSite = 'Lax',
    secure = window.location.protocol === 'https:',
    httpOnly = false,
  } = options

  // URL-encode the value to handle special characters safely
  const encodedValue = encodeURIComponent(value)

  // Build cookie string with security attributes
  let cookieString = `${name}=${encodedValue}; Path=${path}; SameSite=${sameSite}`

  // Add optional attributes
  if (maxAge !== undefined) {
    cookieString += `; Max-Age=${maxAge}`
  }

  if (secure) {
    cookieString += '; Secure'
  }

  if (httpOnly) {
    cookieString += '; HttpOnly'
  }

  // Set the cookie in the browser
  document.cookie = cookieString
}

/**
 * Deletes a cookie by setting its expiration to the past.
 *
 * @param name - The name of the cookie to delete
 * @param options - Optional path and domain to match the original cookie
 *
 * Implementation notes:
 * - Uses Max-Age=0 to immediately expire the cookie
 * - Must match the original cookie's path and domain for successful deletion
 * - Safe to call even if the cookie doesn't exist
 */
export function deleteCookie(
  name: string,
  options: Pick<CookieOptions, 'path'> = {},
): void {
  // Guard against server-side rendering where document doesn't exist
  if (typeof document === 'undefined') return

  const { path = '/' } = options

  // Set cookie with immediate expiration to delete it
  document.cookie = `${name}=; Path=${path}; Max-Age=0`
}

/**
 * Checks if cookies are available and functional in the current environment.
 *
 * @returns True if cookies can be set and retrieved, false otherwise
 *
 * Use cases:
 * - Feature detection before relying on cookie functionality
 * - Graceful degradation when cookies are disabled
 * - Privacy mode detection in some browsers
 */
export function areCookiesEnabled(): boolean {
  // Return false in SSR environment
  if (typeof document === 'undefined') return false

  try {
    // Test cookie functionality with a temporary cookie
    const testKey = '__cookie_test__'
    const testValue = 'test'

    // Attempt to set a test cookie
    setCookie(testKey, testValue, { maxAge: 1 })

    // Check if we can retrieve it
    const retrieved = getCookie(testKey)

    // Clean up the test cookie
    deleteCookie(testKey)

    // Return true if the test was successful
    return retrieved === testValue
  } catch {
    // If any error occurs, assume cookies are not available
    return false
  }
}

/**
 * Safely parses a JSON value from a cookie with error handling.
 *
 * @param name - The name of the cookie containing JSON data
 * @returns Parsed object or null if parsing fails
 *
 * Error handling:
 * - Returns null for missing cookies
 * - Returns null for malformed JSON
 * - Logs parsing errors for debugging
 */
export function getCookieJSON<T = unknown>(name: string): T | null {
  try {
    const cookieValue = getCookie(name)
    if (!cookieValue) return null

    return JSON.parse(cookieValue) as T
  } catch (error) {
    console.error(`Error parsing JSON from cookie '${name}':`, error)
    return null
  }
}

/**
 * Safely stores a JSON object in a cookie with automatic serialization.
 *
 * @param name - The name of the cookie
 * @param data - The object to serialize and store
 * @param options - Optional cookie configuration
 *
 * Features:
 * - Automatic JSON serialization
 * - Error handling for non-serializable objects
 * - Size validation (cookies have ~4KB limit)
 */
export function setCookieJSON(
  name: string,
  data: unknown,
  options: CookieOptions = {},
): boolean {
  try {
    const jsonString = JSON.stringify(data)

    // Warn if cookie might exceed browser limits (~4KB)
    if (jsonString.length > 4000) {
      console.warn(
        `Cookie '${name}' is large (${jsonString.length} chars) and may be rejected by browsers`,
      )
    }

    setCookie(name, jsonString, options)
    return true
  } catch (error) {
    console.error(`Error setting JSON cookie '${name}':`, error)
    return false
  }
}
